import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel, LinearProgress, Stack, TextField, Typography } from "@mui/material";
import { MonitorButton, MonitorDialog } from "../../components/MonitorControls";
import { preselectRelease } from "../boardAdapter";
import { v } from "../../utils/helpers";
import StageErrorAlert from "./StageErrorAlert";
import TargetPicker from "./TargetPicker";
import { initialTargets, isRollout, requestTargets, stageTargetList } from "../targetPreselect";
import TargetValuesWarning from "./TargetValuesWarning";
import { stageLabel } from "../../utils/stages";

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const short = sha => (sha ? String(sha).slice(0, 7) : "");
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};
// Vorstufe von PROD: letzte konfigurierte Stufe davor (Vertrag 10).
const previousStage = board => {
  const keys = (board?.stages || []).filter(stage => stage.configured).map(stage => stage.key);
  const index = keys.indexOf("prod");
  return index > 0 ? keys[index - 1] : null;
};
const labelOf = (board, key) => board?.stages?.find(stage => stage.key === key)?.label || stageLabel(key);
const packageOf = (board, id) => board?.packages?.find(pkg => pkg.packageId === id) || null;

function useAlive() {
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  return alive;
}

function Detail({ children }) {
  return <Typography component="span" variant="body2" sx={{ color: v("text-2") }}>{children}</Typography>;
}

function ExcludedList({ board, excluded, stage }) {
  const { t } = useTranslation("freigabe");
  if (!excluded?.length) return null;
  return <Box component="section">
    <Typography component="h3" variant="subtitle2" sx={{ color: v("text-1"), mb: 1 }}>{t("stageBranches.release.excluded")}</Typography>
    <Stack component="ul" spacing={1} sx={{ listStyle: "none", m: 0, p: 0 }}>
      {excluded.map(row => <Box component="li" key={row.packageId}>
        <Typography variant="body2" sx={{ color: v("text-1"), fontWeight: 600 }}>{packageOf(board, row.packageId)?.packageName || row.packageId}</Typography>
        {/* Ein-Karten-Regel: ein älterer Stand auf der Vorstufe wird nicht angeboten, der Hinweis nennt die Stufe des neueren. */}
        <Detail>{row.reason === "STAGE_NEWER_STATE" ? t("stageBranches.release.newerStage", { stage: labelOf(board, row.stage), source: labelOf(board, stage) })
          : t(`promotion.blocked.${row.reason}`)}</Detail>
      </Box>)}
    </Stack>
  </Box>;
}

// Neue Auswahl: Vorschlag aus preselectRelease, danach frei änderbar.
function SelectionBody({ board, topicId, region, initialPackageIds, onSubmit, onClose, titleId }) {
  const { t } = useTranslation("freigabe");
  const alive = useAlive();
  const stage = previousStage(board);
  const all = useMemo(() => preselectRelease(board, { topicId: null }), [board]);
  const listedIds = all.listed.map(row => row.packageId);
  // Vorbelegung (Korrekturrunde 1 I6): Eine Auswahl im Board wird nie still ersetzt. Gewählt bleibt, was im Release wählbar
  // ist; was nicht wählbar ist (Freeze, PROD-Sperre), steht als Hinweis da. Ohne Board-Auswahl gilt der Vorschlag
  // (nur reached und partial mit anderem Stand als PROD).
  const [initial] = useState(() => {
    if (Array.isArray(initialPackageIds) && initialPackageIds.length)
      return { ids: initialPackageIds.filter(id => listedIds.includes(id)), missing: initialPackageIds.filter(id => !listedIds.includes(id)) };
    return { ids: preselectRelease(board, { topicId }).packageIds, missing: [] };
  });
  const [selected, setSelected] = useState(() => new Set(initial.ids));
  const [date, setDate] = useState(today);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const toggle = id => setSelected(previous => {
    const next = new Set(previous);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const packageIds = listedIds.filter(id => selected.has(id));
  // Mehrzielmodus: Zielliste von PROD, vorbelegt aus Wizard, Region oder Standardziel.
  const targetList = stageTargetList(board, "prod");
  const [preset] = useState(() => initialTargets({ board, targetStage: "prod", sourceStage: stage, packageIds: initial.ids, region }));
  const [targets, setTargets] = useState(preset?.ids ?? null);
  const rollout = isRollout({ board, sourceStage: stage, targetStage: "prod", packageIds });
  const ready = packageIds.length > 0 && DATE.test(date) && !busy && typeof onSubmit === "function";

  async function submit() {
    if (!ready) return;
    setBusy(true); setError(null);
    try { await onSubmit({ packageIds, topicId: topicId ?? null, date, ...(requestTargets(targetList, targets) ? { targets: requestTargets(targetList, targets) } : {}) }); }
    catch (failure) {
      if (alive.current) setError({ code: failure?.code || null, message: failure?.message || "", details: failure?.details });
    } finally { if (alive.current) setBusy(false); }
  }

  return <>
    <DialogTitle id={titleId}>{t("stageBranches.release.title")}</DialogTitle>
    <DialogContent>
      <Stack spacing={2.5}>
        <Typography variant="body2" sx={{ color: v("text-2") }}>{t("stageBranches.release.intro", { stage: labelOf(board, stage) })}</Typography>
        {initial.missing.length > 0 && <Alert severity="info" variant="outlined">{t("stageBranches.release.selectionMissing",
          { names: initial.missing.map(id => packageOf(board, id)?.packageName || id).join(", ") })}</Alert>}
        <Box component="section">
          <Typography component="h3" variant="subtitle2" sx={{ color: v("text-1"), mb: 1 }}>{t("stageBranches.release.packages")}</Typography>
          {all.listed.length === 0 ? <Detail>{t("stageBranches.release.empty", { stage: labelOf(board, stage) })}</Detail>
            : <Stack component="ul" spacing={1} sx={{ listStyle: "none", m: 0, p: 0 }}>
              {all.listed.map(row => {
                const id = row.packageId, pkg = packageOf(board, id), cell = pkg?.cells?.[stage];
                return <Box component="li" key={id} sx={{ borderBottom: `1px solid ${v("border-subtle")}`, pb: 1 }}>
                  <FormControlLabel label={pkg?.packageName || id} disabled={busy}
                    control={<Checkbox checked={selected.has(id)} onChange={() => toggle(id)}/>}/>
                  <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ pl: 4 }}>
                    <Detail>{t("stageBranches.release.tree", { tree: short(cell?.tree) })}</Detail>
                    <Detail>{cell?.proof?.runId ? t("stageBranches.release.run", { stage: labelOf(board, stage), runId: cell.proof.runId })
                      : t("stageBranches.release.noRun", { stage: labelOf(board, stage) })}</Detail>
                  </Stack>
                  {/* Hinweise statt Sperre: gleicher Stand wie PROD (erneutes Ausrollen) bzw. nicht grüner Lauf, wählbar, nicht vorausgewählt. */}
                  {row.sameAsProd && <Typography component="p" variant="body2" sx={{ color: v("text-2"), pl: 4, m: 0 }}>
                    {t("stageBranches.release.sameAsProd")}</Typography>}
                  {!["reached", "partial"].includes(row.state) && <Typography component="p" variant="body2" role="status" sx={{ color: v("warning"), pl: 4, m: 0 }}>
                    {t("stageBranches.release.notGreen", { stage: labelOf(board, stage) })}</Typography>}
                </Box>;
              })}
            </Stack>}
        </Box>
        <ExcludedList board={board} excluded={all.excluded} stage={stage}/>
        {targetList && targets && <TargetPicker targets={targetList} value={targets} onChange={setTargets} reason={preset?.reason} disabled={busy}/>}
        {targetList && <TargetValuesWarning board={board} sourceStage={stage} targetStage="prod" packageIds={packageIds} targetIds={targets}/>}
        {rollout && targetList && <Alert severity="info" variant="outlined"><strong>{t("stageBranches.targets.rollout")}</strong>{" "}
          {t("stageBranches.targets.rolloutHint", { stage: labelOf(board, "prod") })}</Alert>}
        <TextField type="date" size="small" required disabled={busy} value={date} label={t("stageBranches.release.date")}
          InputLabelProps={{ shrink: true }} onChange={event => setDate(event.target.value)} sx={{ maxWidth: 240 }}/>
        <StageErrorAlert error={error} title={t("stageBranches.release.submitError")}/>
      </Stack>
    </DialogContent>
    <DialogActions>
      <MonitorButton autoFocus disabled={busy} onClick={onClose}>{t("stageBranches.release.cancel")}</MonitorButton>
      <MonitorButton variant="contained" disabled={!ready} onClick={submit}>
        {t(busy ? "stageBranches.release.sending" : "stageBranches.release.submit")}
      </MonitorButton>
    </DialogActions>
  </>;
}

// Gestellter Antrag: festgehaltene Trees und Läufe; „Auswahl aktualisieren“ erzeugt einen neuen Antrag (Vertrag 10.1).
function RequestBody({ board, request, onRefresh, onClose, titleId }) {
  const { t } = useTranslation("freigabe");
  const alive = useAlive();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const stage = request.sourceStage || previousStage(board);
  const items = request.pin?.items || [];
  const canRefresh = request.status === "pending" && typeof onRefresh === "function";

  async function refresh() {
    if (!canRefresh || busy) return;
    setBusy(true); setError(null);
    try { await onRefresh(request); }
    catch (failure) {
      if (alive.current) setError({ code: failure?.code || null, message: failure?.message || "", details: failure?.details });
    } finally { if (alive.current) setBusy(false); }
  }

  return <>
    <DialogTitle id={titleId}>{t("stageBranches.release.titleRequest", { tag: request.release?.tag || request.pin?.release?.tag || "" })}</DialogTitle>
    <DialogContent>
      <Stack spacing={2.5}>
        <Box component="section">
          <Typography component="h3" variant="subtitle2" sx={{ color: v("text-1"), mb: 1 }}>{t("stageBranches.release.pinned")}</Typography>
          <Stack component="ul" spacing={1} sx={{ listStyle: "none", m: 0, p: 0 }}>
            {items.map(item => {
              const pinned = item.entries?.find(entry => entry.kind === "package")?.sourceTree || null;
              const proof = item.proofs?.find(row => row.stage === stage) || item.proofs?.[0] || null;
              const current = packageOf(board, item.packageId)?.cells?.[stage]?.tree || null;
              return <Box component="li" key={item.packageId} sx={{ borderBottom: `1px solid ${v("border-subtle")}`, pb: 1 }}>
                <Typography variant="body2" sx={{ color: v("text-1"), fontWeight: 600 }}>{item.packageName || item.packageId}</Typography>
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  <Detail>{t("stageBranches.release.tree", { tree: short(pinned) })}</Detail>
                  <Detail>{proof?.runId ? t("stageBranches.release.run", { stage: labelOf(board, stage), runId: proof.runId })
                    : t("stageBranches.release.noRun", { stage: labelOf(board, stage) })}</Detail>
                </Stack>
                {current && pinned && current !== pinned && <Typography variant="body2" sx={{ color: v("warning") }}>
                  {t("stageBranches.release.changed", { stage: labelOf(board, stage) })}
                </Typography>}
              </Box>;
            })}
          </Stack>
        </Box>
        <ExcludedList board={board} excluded={request.release?.preselection?.excluded}/>
        {canRefresh && <Alert severity="info" variant="outlined">{t("stageBranches.release.refreshHint")}</Alert>}
        <StageErrorAlert error={error} title={t("stageBranches.release.refreshError")}/>
      </Stack>
    </DialogContent>
    <DialogActions>
      <MonitorButton autoFocus disabled={busy} onClick={onClose}>{t("stageBranches.release.close")}</MonitorButton>
      {canRefresh && <MonitorButton variant="contained" disabled={busy} onClick={refresh}>
        {t(busy ? "stageBranches.release.refreshing" : "stageBranches.release.refresh")}
      </MonitorButton>}
    </DialogActions>
  </>;
}

/** Releaseauswahl nach PROD (Vertrag 10, 16.2): neue Auswahl oder Ansicht eines gestellten Release-Antrags. */
export default function ReleaseSelectionDialog({ open, board, region = null, topicId = null, request = null, initialPackageIds, onSubmit, onRefresh, onClose, returnFocusRef }) {
  const { t } = useTranslation("freigabe");
  const titleId = useId();
  if (!open) return null;
  // Fokus nach dem Schließen an die von der Seite übergebene Stelle (wie PromotionFreeze); sonst stellt MUI ihn wieder her.
  const close = () => { onClose?.(); if (returnFocusRef) requestAnimationFrame(() => returnFocusRef.current?.focus?.()); };
  return <MonitorDialog className="promotion-dialog sb-dialog" maxWidth="md" aria-labelledby={titleId} onClose={close}>
    {!board ? <>
      <DialogTitle id={titleId}>{t(request ? "stageBranches.release.titleRequest" : "stageBranches.release.title", { tag: request?.release?.tag || "" })}</DialogTitle>
      <DialogContent><LinearProgress aria-label={t("stageBranches.release.loading")}/></DialogContent>
      <DialogActions><MonitorButton autoFocus onClick={close}>{t("stageBranches.release.cancel")}</MonitorButton></DialogActions>
    </> : request ? <RequestBody board={board} request={request} onRefresh={onRefresh} onClose={close} titleId={titleId}/>
      : <SelectionBody board={board} topicId={topicId} region={region} initialPackageIds={initialPackageIds} onSubmit={onSubmit} onClose={close} titleId={titleId}/>}
  </MonitorDialog>;
}
